import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';

import { Button, IconButton } from '@/components';

import { RangeSlider } from '../controls/RangeSlider';
import type { LayoutElementStyle } from '../styleTypes';

interface FiltersSectionProps {
  style: LayoutElementStyle;
  onChange: (updates: Partial<LayoutElementStyle>) => void;
}

interface FilterSliderProps {
  label: string;
  value: number | undefined;
  defaultValue: number;
  min: number;
  max: number;
  unit: string;
  onChange: (v: number | undefined) => void;
}

function FilterSlider({ label, value, defaultValue, min, max, unit, onChange }: FilterSliderProps) {
  const { t } = useTranslation();
  const current = value ?? defaultValue;
  const isDefault = value === undefined || value === defaultValue;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-gray-400 font-mono w-10 text-right">
            {current}
            {unit}
          </span>
          {!isDefault && (
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={t('tenant.systemModuleBuilder.style.common.reset')}
              onClick={() => onChange(undefined)}
              className="h-4 w-4 text-gray-400 hover:text-accent-500"
            >
              <RotateCcw className="w-3 h-3" />
            </IconButton>
          )}
        </div>
      </div>
      <RangeSlider
        min={min}
        max={max}
        value={current}
        onChange={(v) => {
          const rounded = Math.round(v);
          onChange(rounded === defaultValue ? undefined : rounded);
        }}
        className="w-full"
        aria-label={label}
      />
    </div>
  );
}

export function FiltersSection({ style, onChange }: FiltersSectionProps) {
  const { t } = useTranslation();
  const p = 'tenant.systemModuleBuilder.style.';
  const hasAnyFilter =
    style.filterBlur ||
    style.filterBrightness ||
    style.filterContrast ||
    style.filterSaturate ||
    style.filterGrayscale ||
    style.filterHueRotate;

  return (
    <div className="space-y-3">
      {/* Preview */}
      {hasAnyFilter && (
        <div className="flex justify-center py-2">
          <div
            className="w-24 h-16 rounded-lg bg-gradient-to-br from-accent-400 to-accent-500"
            style={{
              filter:
                [
                  style.filterBlur ? `blur(${style.filterBlur}px)` : '',
                  style.filterBrightness !== undefined
                    ? `brightness(${style.filterBrightness}%)`
                    : '',
                  style.filterContrast !== undefined ? `contrast(${style.filterContrast}%)` : '',
                  style.filterSaturate !== undefined ? `saturate(${style.filterSaturate}%)` : '',
                  style.filterGrayscale ? `grayscale(${style.filterGrayscale}%)` : '',
                  style.filterHueRotate ? `hue-rotate(${style.filterHueRotate}deg)` : '',
                ]
                  .filter(Boolean)
                  .join(' ') || undefined,
            }}
          />
        </div>
      )}

      <FilterSlider
        label={t(`${p}filters.blur`)}
        value={style.filterBlur}
        defaultValue={0}
        min={0}
        max={20}
        unit="px"
        onChange={(v) => onChange({ filterBlur: v })}
      />
      <FilterSlider
        label={t(`${p}filters.brightness`)}
        value={style.filterBrightness}
        defaultValue={100}
        min={0}
        max={200}
        unit="%"
        onChange={(v) => onChange({ filterBrightness: v })}
      />
      <FilterSlider
        label={t(`${p}filters.contrast`)}
        value={style.filterContrast}
        defaultValue={100}
        min={0}
        max={200}
        unit="%"
        onChange={(v) => onChange({ filterContrast: v })}
      />
      <FilterSlider
        label={t(`${p}filters.saturate`)}
        value={style.filterSaturate}
        defaultValue={100}
        min={0}
        max={200}
        unit="%"
        onChange={(v) => onChange({ filterSaturate: v })}
      />
      <FilterSlider
        label={t(`${p}filters.grayscale`)}
        value={style.filterGrayscale}
        defaultValue={0}
        min={0}
        max={100}
        unit="%"
        onChange={(v) => onChange({ filterGrayscale: v })}
      />
      <FilterSlider
        label={t(`${p}filters.hueRotate`)}
        value={style.filterHueRotate}
        defaultValue={0}
        min={0}
        max={360}
        unit="°"
        onChange={(v) => onChange({ filterHueRotate: v })}
      />

      {hasAnyFilter && (
        <Button
          type="button"
          variant="ghost"
          onClick={() =>
            onChange({
              filterBlur: undefined,
              filterBrightness: undefined,
              filterContrast: undefined,
              filterSaturate: undefined,
              filterGrayscale: undefined,
              filterHueRotate: undefined,
            })
          }
          className="w-full rounded border border-gray-300 py-1.5 text-xs font-normal text-gray-500 hover:border-red-300 hover:text-red-500 dark:border-gray-600"
        >
          {t(`${p}filters.resetAll`)}
        </Button>
      )}
    </div>
  );
}
